/**
 * Runtime brand loader for white-label deployments.
 *
 * At boot, the frontend fetches `/brand.json` from the same origin. Each
 * tenant's Static Web App drops a different `brand.json` into `public/`
 * (or at the static-site build step) — same immutable app shell, different
 * wordmark / colours / tagline / login-button label.
 *
 * Why runtime JSON and not build-time env vars?
 *   - One image / one bundle → N tenants. Rebranding doesn't require a
 *     rebuild; the SWA origin just swaps the file.
 *   - Ops can A/B test a colour change without a git commit.
 *   - Per-tenant SWA in tenant.bicep serves a tenant-specific bundle
 *     that includes *only* the brand.json it cares about.
 *
 * The loader merges fetched overrides onto `DEFAULT_BRAND`, so `brand.json`
 * can omit any field it wants to inherit. Missing or malformed files fall
 * back silently to defaults — the shell must still render without Azure.
 *
 * CSS custom properties listed in `cssVarMap` are written onto the
 * document element's inline style, which takes precedence over the
 * `:root{…}` rules in `styles/theme.css`. This is why the default brand
 * still lives in CSS — it's the zero-JS fallback — while brand.json can
 * override individual tokens.
 */

export interface BrandConfig {
  /** Shown in the header + document title. */
  productName: string;
  /** One-line subtitle under the product name. */
  tagline: string;
  /** Logo (any `img`-compatible URL, including data: URIs). Empty = no logo. */
  logoUrl: string;
  /**
   * Reversed logo, for placing on a dark photograph or panel. Optional:
   * a brand that ships one mark leaves it empty and callers fall back to
   * `logoUrl`. Exists because the welcome screen puts the mark on a
   * blue-hour photo, where a dark-ink logo disappears.
   */
  logoUrlInverse: string;
  /**
   * Optional glyph composited *inside* the aurora squircle mark — a tenant's
   * monogram sitting on the brand gradient, rather than `logoUrl`, which
   * replaces the mark with a plain image. Should be a transparent PNG/SVG of
   * the glyph alone (no background plate), light-coloured: it is drawn over
   * the accent gradient. Empty = a bare gradient mark, as before.
   */
  markGlyphUrl: string;
  /** `<title>` format string. `{product}` is replaced with `productName`. */
  documentTitle: string;
  /** Colour + radius tokens. Keys map to CSS custom properties (see `cssVarMap`). */
  theme: BrandTheme;
  /**
   * Phase 6 — optional dark-mode override. The brand-to-primevue adapter
   * uses this to build the dark-side palette. When absent, the adapter
   * derives a dark palette from `theme` (luminance-flip on bg/surface,
   * accent stays); when present, every key is taken as an explicit override.
   */
  themeDark?: BrandTheme;
  /** Label for the Entra login button ("Sign in with <org>"). Empty = default copy. */
  loginButtonLabel: string;
  /**
   * The AI-transparency notice shown under the composer and carried into
   * every export. Required by the EU AI Act for a limited-risk system: the
   * user has to be told the output is machine-generated and needs checking.
   *
   * Brand-configurable rather than hardcoded because it is the one string in
   * the UI a customer's compliance function may need to word themselves —
   * and because it has to be readable in the deployment's own language.
   * Setting it to an empty string in `brand.json` removes the notice, which
   * a deployment should only do when the same disclosure is made somewhere
   * else; there is no legal default that covers everyone.
   */
  aiDisclosure: string;
}

export interface BrandTheme {
  colorBg?: string;
  colorSurface?: string;
  colorSurfaceAlt?: string;
  colorBorder?: string;
  colorInk?: string;
  colorMuted?: string;
  colorAccent?: string;
  colorAccentHover?: string;
  colorAccentDim?: string;
  /** Interactive accent — focus rings, links, citation chips, active state.
   *  Separate from `colorAccent` because a brand's primary is often a deep
   *  colour meant for fills and text, while the thing that says "this is
   *  live, this is yours" needs to be brighter and used sparingly. Danoffice
   *  IT's guidelines say exactly that of Sea Blue: an accent paired with
   *  Primary Blue, never a primary in its own right. Falls back to
   *  colorAccent when a brand does not set it. */
  colorHighlight?: string;
  colorHighlightHover?: string;
  colorSuccess?: string;
  colorError?: string;
  radiusSm?: string;
  radiusMd?: string;
  radiusLg?: string;
}

/**
 * Map of `BrandTheme` keys → CSS custom property names. Exported because the
 * test files need to assert each override landed on the right variable.
 */
export const cssVarMap: Record<keyof BrandTheme, string> = {
  colorBg: "--color-bg",
  colorSurface: "--color-surface",
  colorSurfaceAlt: "--color-surface-alt",
  colorBorder: "--color-border",
  colorInk: "--color-ink",
  colorMuted: "--color-muted",
  colorAccent: "--color-accent",
  colorAccentHover: "--color-accent-hover",
  colorAccentDim: "--color-accent-dim",
  colorHighlight: "--color-highlight",
  colorHighlightHover: "--color-highlight-hover",
  colorSuccess: "--color-success",
  colorError: "--color-error",
  radiusSm: "--radius-sm",
  radiusMd: "--radius-md",
  radiusLg: "--radius-lg",
};

export const DEFAULT_BRAND: BrandConfig = {
  productName: "Choir",
  tagline: "Internal AI assistants for your team",
  logoUrl: "",
  logoUrlInverse: "",
  markGlyphUrl: "",
  documentTitle: "{product}",
  theme: {},
  loginButtonLabel: "",
  aiDisclosure:
    "Answers are AI-generated and can be wrong \u2014 check them against " +
    "the cited sources before you use them.",
};

/**
 * Shallow-merge an incoming (possibly partial) override onto defaults. Nested
 * `theme` is merged one level deep — we don't need full recursion here.
 */
export function mergeBrand(
  base: BrandConfig,
  override: Partial<BrandConfig> | null | undefined,
): BrandConfig {
  if (!override) return base;
  return {
    ...base,
    ...override,
    theme: { ...base.theme, ...(override.theme ?? {}) },
  };
}

/**
 * Type-guard + normaliser. Drops unknown keys + coerces any non-string value
 * back to its default. Defensive because `brand.json` is operator-authored
 * at runtime; a typo shouldn't wedge the whole app.
 */
export function sanitiseBrand(raw: unknown): Partial<BrandConfig> {
  if (typeof raw !== "object" || raw === null) return {};
  const r = raw as Record<string, unknown>;
  const out: Partial<BrandConfig> = {};
  if (typeof r.productName === "string") out.productName = r.productName;
  if (typeof r.tagline === "string") out.tagline = r.tagline;
  if (typeof r.logoUrl === "string") out.logoUrl = r.logoUrl;
  if (typeof r.logoUrlInverse === "string")
    out.logoUrlInverse = r.logoUrlInverse;
  if (typeof r.markGlyphUrl === "string") out.markGlyphUrl = r.markGlyphUrl;
  if (typeof r.documentTitle === "string") out.documentTitle = r.documentTitle;
  if (typeof r.loginButtonLabel === "string") {
    out.loginButtonLabel = r.loginButtonLabel;
  }
  if (typeof r.theme === "object" && r.theme !== null) {
    const theme: BrandTheme = {};
    const src = r.theme as Record<string, unknown>;
    for (const key of Object.keys(cssVarMap) as (keyof BrandTheme)[]) {
      const v = src[key];
      if (typeof v === "string" && v.trim().length > 0) {
        theme[key] = v;
      }
    }
    out.theme = theme;
  }
  if (typeof r.themeDark === "object" && r.themeDark !== null) {
    const themeDark: BrandTheme = {};
    const src = r.themeDark as Record<string, unknown>;
    for (const key of Object.keys(cssVarMap) as (keyof BrandTheme)[]) {
      const v = src[key];
      if (typeof v === "string" && v.trim().length > 0) {
        themeDark[key] = v;
      }
    }
    out.themeDark = themeDark;
  }
  return out;
}

/**
 * Apply a resolved brand to the live DOM:
 *   - Inject CSS custom properties onto `documentElement.style`.
 *   - Update the document title via the `documentTitle` template.
 *   - Does NOT touch the DOM body — components read the brand out of the
 *     returned config (or from `brandRef` in main.ts) for product name /
 *     tagline / logo.
 */
export function applyBrand(brand: BrandConfig, doc: Document = document): void {
  const root = doc.documentElement;
  for (const [key, cssVar] of Object.entries(cssVarMap) as [
    keyof BrandTheme,
    string,
  ][]) {
    const v = brand.theme[key];
    if (v) {
      root.style.setProperty(cssVar, v);
    } else {
      root.style.removeProperty(cssVar);
    }
  }
  if (brand.documentTitle) {
    doc.title = brand.documentTitle.replace("{product}", brand.productName);
  }
}

/**
 * Fetch `/brand.json` (override path via `url`). Returns `DEFAULT_BRAND`
 * when the file is missing, not JSON, or returns a non-2xx — the app must
 * still boot on a bare origin with no brand file.
 *
 * Pass `fetchImpl` in tests to simulate responses without a real network.
 */
export async function loadBrand(
  url = "/brand.json",
  fetchImpl: typeof fetch = fetch,
): Promise<BrandConfig> {
  try {
    const resp = await fetchImpl(url, { cache: "no-cache" });
    if (!resp.ok) return DEFAULT_BRAND;
    const raw = await resp.json();
    return mergeBrand(DEFAULT_BRAND, sanitiseBrand(raw));
  } catch {
    return DEFAULT_BRAND;
  }
}
